import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import { FileDownload as FileDownloadIcon, Upload as UploadIcon, Check as CheckIcon, Error as ErrorIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import Layout from '../../components/layout/Layout';
import axiosInstance from '../../utils/axios';
import { useSnackbar } from 'notistack';

const PhysicalStockUpdate = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(''); // '' => Warehouse
  const [loading, setLoading] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [applying, setApplying] = useState(false);

  const hasChanges = useMemo(() => previewRows.some(r => r._change), [previewRows]);

  const formatCurrency = (v) => {
    const n = Number(v || 0);
    if (Number.isNaN(n)) return '0.00';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(n);
  };

  const downloadTemplate = async () => {
    try {
      setLoading(true);
      let rows = [];
      if (!selectedLocationId) {
        const res = await axiosInstance.get('/products/management', { params: { page: 0, size: 10000, sort: 'name', direction: 'ASC' } });
        const content = res.data?.content || [];
        rows = content.map(p => ({
          productId: p.id,
          productName: p.name,
          sku: p.sku || '',
          barcode: p.barcode || '',
          purchasePrice: p.purchasePrice ?? 0,
          sellingPrice: p.sellingPrice ?? 0,
          quantity: p.quantity ?? 0
        }));
      } else {
        const res = await axiosInstance.get('/products/location-stock-update/list', { params: { locationId: selectedLocationId } });
        const content = res.data || [];
        rows = content.map(p => ({
          productId: p.productId,
          productName: p.productName,
          sku: p.sku || '',
          barcode: p.barcode || '',
          purchasePrice: 0,
          sellingPrice: p.sellingPrice ?? 0,
          quantity: Number(p.quantity ?? 0)
        }));
      }
      setProducts(rows);

      // Build worksheet
      const headers = ['productId', 'productName', 'sku', 'barcode', 'purchasePrice', 'sellingPrice', 'quantity'];
      const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, selectedLocationId ? 'LocationProducts' : 'Products');
      const date = new Date().toISOString().split('T')[0];
      const name = selectedLocationId ? (locations.find(l => String(l.id) === String(selectedLocationId))?.name || 'Location') : 'Warehouse';
      XLSX.writeFile(wb, `PhysicalStockTemplate_${name}_${date}.xlsx`);
      enqueueSnackbar('Template downloaded', { variant: 'success' });
    } catch (e) {
      console.error('Download template failed', e);
      enqueueSnackbar('Failed to download template', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadScopeRows = async () => {
    try {
      let rows = [];
      if (!selectedLocationId) {
        const res = await axiosInstance.get('/products/management', { params: { page: 0, size: 10000, sort: 'name', direction: 'ASC' } });
        const content = res.data?.content || [];
        rows = content.map(p => ({
          productId: p.id,
          productName: p.name,
          sku: p.sku || '',
          barcode: p.barcode || '',
          purchasePrice: p.purchasePrice ?? 0,
          sellingPrice: p.sellingPrice ?? 0,
          quantity: p.quantity ?? 0
        }));
      } else {
        const res = await axiosInstance.get('/products/location-stock-update/list', { params: { locationId: selectedLocationId } });
        const content = res.data || [];
        rows = content.map(p => ({
          productId: p.productId,
          productName: p.productName,
          sku: p.sku || '',
          barcode: p.barcode || '',
          purchasePrice: 0,
          sellingPrice: p.sellingPrice ?? 0,
          quantity: Number(p.quantity ?? 0)
        }));
      }
      setProducts(rows);
    } catch (e) {
      console.error('Failed to refresh scope rows', e);
    }
  };

  const ensureProductsLoaded = async () => {
    if (products && products.length > 0) return products;
    try {
      const res = await axiosInstance.get('/products/management', { params: { page: 0, size: 10000, sort: 'name', direction: 'ASC' } });
      const content = res.data?.content || [];
      const rows = content.map(p => ({
        productId: p.id,
        productName: p.name,
        sku: p.sku || '',
        barcode: p.barcode || '',
        purchasePrice: p.purchasePrice ?? 0,
        sellingPrice: p.sellingPrice ?? 0,
        quantity: p.quantity ?? 0
      }));
      setProducts(rows);
      return rows;
    } catch (e) {
      console.error('Failed to fetch products for validation', e);
      enqueueSnackbar('Failed to fetch products for validation', { variant: 'error' });
      return [];
    }
  };

  const onUpload = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });

      // Validate and prepare preview rows
      const baseProducts = products.length ? products : await ensureProductsLoaded();
      const byId = new Map(baseProducts.map(p => [String(p.productId), p]));
      const rows = json.map((r, idx) => {
        // Some spreadsheets format numeric IDs with decimals; normalize to integer-like string
        const rawId = r.productId ?? '';
        const normalizedId = rawId === '' ? '' : String(Number(rawId)).split('.')[0];
        const id = String(normalizedId).trim();
        const base = byId.get(id);
        const errors = [];
        if (!id) errors.push('Missing productId');
        if (!base) errors.push('Unknown productId');

        const newPurchase = Number(r.purchasePrice);
        const newSelling = Number(r.sellingPrice);
        const newQty = Number(r.quantity);
        if (Number.isNaN(newPurchase) || newPurchase < 0) errors.push('Invalid purchasePrice');
        if (Number.isNaN(newSelling) || newSelling < 0) errors.push('Invalid sellingPrice');
        if (Number.isNaN(newQty) || newQty < 0) errors.push('Invalid quantity');

        const oldPurchase = base?.purchasePrice ?? 0;
        const oldSelling = base?.sellingPrice ?? 0;
        const oldQty = base?.quantity ?? 0;
        const changed = base && (!selectedLocationId ? (oldPurchase !== newPurchase || oldSelling !== newSelling || oldQty !== newQty) : (oldQty !== newQty));

        return {
          productId: id,
          productName: base?.productName ?? r.productName ?? '',
          sku: base?.sku ?? r.sku ?? '',
          barcode: base?.barcode ?? r.barcode ?? '',
          oldPurchase, newPurchase,
          oldSelling, newSelling,
          oldQty, newQty,
          status: errors.length ? 'INVALID' : changed ? 'CHANGED' : 'NO_CHANGE',
          error: errors.join(', '),
          _change: changed && errors.length === 0
        };
      });
      setPreviewRows(rows);
      enqueueSnackbar('File parsed successfully', { variant: 'success' });
    } catch (e) {
      console.error('Upload parse failed', e);
      enqueueSnackbar('Failed to parse uploaded file', { variant: 'error' });
    } finally {
      e.target.value = '';
    }
  };

  const applyUpdates = async () => {
    try {
      setApplying(true);
      const changedRows = previewRows.filter(r => r._change);
      if (!changedRows.length) {
        enqueueSnackbar('No changes to apply', { variant: 'info' });
        return;
      }
      let res;
      if (!selectedLocationId) {
        const payload = changedRows.map(r => ({
          productId: Number(r.productId),
          purchasePrice: r.newPurchase,
          sellingPrice: r.newSelling,
          quantity: r.newQty
        }));
        res = await axiosInstance.post('/products/stock-update/apply', payload);
      } else {
        const payload = changedRows.map(r => ({
          productId: Number(r.productId),
          quantity: r.newQty
        }));
        res = await axiosInstance.post('/products/location-stock-update/apply', payload, { params: { locationId: selectedLocationId } });
      }
      const applied = res.data?.applied || changedRows.length;
      enqueueSnackbar(`Applied ${applied} updates`, { variant: 'success' });
      // Reset view for next operation
      setPreviewRows([]);
      await loadScopeRows();
    } catch (e) {
      console.error('Apply updates failed', e);
      enqueueSnackbar('Failed to apply updates', { variant: 'error' });
    } finally {
      setApplying(false);
    }
  };

  useEffect(() => {
    // Load locations list for dropdown
    (async () => {
      try {
        const res = await axiosInstance.get('/locations', { params: { page: 0, size: 1000, sort: 'name', direction: 'ASC' } });
        setLocations(res.data?.content || []);
      } catch (e) {
        console.error('Failed to load locations', e);
      }
    })();
  }, []);

  useEffect(() => {
    // When scope changes, clear preview and load fresh rows
    setPreviewRows([]);
    loadScopeRows();
  }, [selectedLocationId]);

  return (
    <Layout>
      <Container maxWidth="xl">
        <Box sx={{ py: 2 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>Physical Stock Update</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Download the Excel, update purchase/selling price and quantity, then upload to preview and apply.
          </Typography>
          <Card>
            <CardContent>
              {/* Centered scope selector */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Paper elevation={0} sx={{ px: 2, py: 1.5, border: '1px solid', borderColor: 'primary.main', bgcolor: 'rgba(25,118,210,0.06)', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip label="Scope" color="primary" size="small" />
                  <FormControl size="small" sx={{ minWidth: 260 }}>
                    <Select
                      value={selectedLocationId}
                      onChange={(e) => setSelectedLocationId(e.target.value)}
                      displayEmpty
                      renderValue={(val) => {
                        if (val === '' || val === undefined || val === null) return 'Warehouse';
                        const loc = locations.find(l => String(l.id) === String(val));
                        return loc?.name || 'Warehouse';
                      }}
                    >
                      <MenuItem value="">Warehouse</MenuItem>
                      {locations.map(l => (
                        <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Box>

              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} /> : <FileDownloadIcon />} onClick={downloadTemplate} disabled={loading}>
                    {loading ? 'Preparing…' : 'Download Excel'}
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant="contained" component="label" startIcon={<UploadIcon />}>
                    Upload Excel
                    <input type="file" accept=".xlsx,.xls" hidden onChange={onUpload} />
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant="contained" color="success" onClick={applyUpdates} disabled={!hasChanges || applying} startIcon={applying ? <CircularProgress size={16} /> : <CheckIcon />}>
                    {applying ? 'Applying…' : 'Apply Updates'}
                  </Button>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product ID</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Barcode</TableCell>
                      {!selectedLocationId && <TableCell align="right">Old Purchase</TableCell>}
                      {!selectedLocationId && <TableCell align="right">New Purchase</TableCell>}
                      <TableCell align="right">Old Selling</TableCell>
                      <TableCell align="right">New Selling</TableCell>
                      <TableCell align="right">Old Qty</TableCell>
                      <TableCell align="right">New Qty</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center">No preview yet. Upload an Excel to preview changes.</TableCell>
                      </TableRow>
                    ) : (
                      previewRows.map((r, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{r.productId}</TableCell>
                          <TableCell>{r.productName}</TableCell>
                          <TableCell>{r.sku}</TableCell>
                          <TableCell>{r.barcode}</TableCell>
                          {!selectedLocationId && (<TableCell align="right">{formatCurrency(r.oldPurchase)}</TableCell>)}
                          {!selectedLocationId && (<TableCell align="right" style={{ color: r.oldPurchase !== r.newPurchase ? '#1976d2' : undefined }}>{formatCurrency(r.newPurchase)}</TableCell>)}
                          <TableCell align="right">{formatCurrency(r.oldSelling)}</TableCell>
                          <TableCell align="right" style={{ color: r.oldSelling !== r.newSelling ? '#1976d2' : undefined }}>{formatCurrency(r.newSelling)}</TableCell>
                          <TableCell align="right">{r.oldQty}</TableCell>
                          <TableCell align="right" style={{ color: r.oldQty !== r.newQty ? '#1976d2' : undefined }}>{r.newQty}</TableCell>
                          <TableCell>
                            {r.status === 'INVALID' ? (
                              <Chip label={r.error || 'Invalid'} color="error" size="small" icon={<ErrorIcon />} />
                            ) : r.status === 'CHANGED' ? (
                              <Chip label="Changed" color="info" size="small" />
                            ) : (
                              <Chip label="No Change" size="small" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Layout>
  );
};

export default PhysicalStockUpdate;


